import { onBeforeUnmount, ref } from "vue";

const DISMISSED_KEY = "eco_install_dismissed";

// Форма события есть только в Chrome, в lib.dom.d.ts пока не добавлена.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isStandalone = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  // Нестандартный флаг iOS Safari — beforeinstallprompt там вообще не бывает,
  // но так можно распознать уже установленное состояние.
  (navigator as { standalone?: boolean }).standalone === true;

// iPadOS 13+ отдаёт UA как обычный "Macintosh", неотличимый от настоящего Mac
// по строке UA — maxTouchPoints их различает (у настоящего Mac всегда 0).
const isIos = (): boolean => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
};

const isDismissed = (): boolean => localStorage.getItem(DISMISSED_KEY) === "1";

export function useInstallPrompt() {
  const canInstall = ref(false);
  // На iOS (Safari/Chrome/Firefox — все на WebKit) beforeinstallprompt не бывает
  // вообще, программного триггера установки нет, только ручной Share → "На экран
  // «Домой»" — поэтому здесь просто показываются инструкции вместо кнопки действия.
  const showIosInstructions = ref(isIos() && !isStandalone() && !isDismissed());
  let deferredEvent: BeforeInstallPromptEvent | null = null;

  const handleBeforeInstallPrompt = (event: Event) => {
    event.preventDefault();
    if (isStandalone() || isDismissed()) return;
    deferredEvent = event as BeforeInstallPromptEvent;
    canInstall.value = true;
  };

  const handleAppInstalled = () => {
    deferredEvent = null;
    canInstall.value = false;
  };

  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);

  onBeforeUnmount(() => {
    window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.removeEventListener("appinstalled", handleAppInstalled);
  });

  const promptInstall = async () => {
    if (!deferredEvent) return;
    const event = deferredEvent;
    deferredEvent = null;
    canInstall.value = false;
    await event.prompt();
    await event.userChoice;
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    deferredEvent = null;
    canInstall.value = false;
    showIosInstructions.value = false;
  };

  return { canInstall, showIosInstructions, promptInstall, dismiss };
}
